import httpInstances from '@/api/axiosInstance';
import type { SymbolKey } from '@/entities/symbol';
import type {
  CsvWidgetExportAdditionalParams,
  CsvWidgetExportSource
} from '@/features/DataHub/modules/Export/types/context.types';
import type { ExportItemType } from '@/features/DataHub/types/pastExports';
import { isSourceBackedDataExport } from '@/features/DataHub/utils/isSourceBackedDataExport';
import {
  normalizeSourceBackedDataExportFrom,
  resolveSourceBackedDataExportSource
} from '@/features/DataHub/utils/sourceBackedDataExport';
import { detectEnvironment } from '@/utils/detectEnvironment';

type LegacySourceSettings = {
  source: CsvWidgetExportSource;
  from?: string | null;
  to?: string | null;
  tabName?: string;
};

type PastExportsResponseItem = Omit<ExportItemType, 'sourceSettings'> & {
  sourceSettings?: LegacySourceSettings;
  tradeSettings?: LegacySourceSettings;
};

type PastExportsResponse = Record<string, PastExportsResponseItem>;

export type PastExportDetails = {
  name: string;
  type: string;
  exportSubType: string | null;
  exportTimestamp: number;
  dateFormat: string;
  from: string | null;
  to: string | null;
  metadata: string;
  timezone: string;
  symbols: SymbolKey[];
  additionalParams?: CsvWidgetExportAdditionalParams;
  sourceSettings?: {
    source: CsvWidgetExportSource;
    from?: string | null;
    to?: string | null;
    tabName?: string;
  };
};

const mapSymbols = (symbols: ExportItemType['symbols']): SymbolKey[] => {
  return (symbols ?? []).map(symbol => ({
    Code: symbol.key.code,
    Period: symbol.key.period,
    TimeRef: symbol.key.timeRef,
    PeriodType: symbol.key.periodType
  }));
};

export const mapExportItemToDetails = (item: ExportItemType): PastExportDetails => {
  const resolvedSource = resolveSourceBackedDataExportSource(item);
  const sourceBackedFrom = item.sourceSettings
    ? item.sourceSettings.from
    : item.exportSettings.from;
  const sourceBackedTo = item.sourceSettings
    ? item.sourceSettings.to
    : (item.exportSettings.to ?? null);
  const normalizedSourceBackedFrom = normalizeSourceBackedDataExportFrom(
    sourceBackedFrom,
    sourceBackedTo
  );

  return {
    name: item.name,
    type: item.exportType,
    exportSubType: item.exportSubType,
    exportTimestamp: item.lastExecutionTime,
    dateFormat: item.exportSettings.dateFormat,
    from: isSourceBackedDataExport(item) ? normalizedSourceBackedFrom : item.exportSettings.from,
    to: isSourceBackedDataExport(item) ? (sourceBackedTo ?? null) : (item.exportSettings.to ?? ''),
    metadata: item.exportSettings.includeMetadata ? 'true' : 'false',
    timezone: item.exportSettings.timezone,
    symbols: mapSymbols(item.symbols),
    additionalParams: item.additionalParams,
    sourceSettings: resolvedSource
      ? {
          source: resolvedSource,
          from: normalizedSourceBackedFrom,
          to: sourceBackedTo,
          tabName: item.sourceSettings?.tabName
        }
      : undefined
  };
};

const getExportConfigPath = () =>
  detectEnvironment() === 'staging' ? '/config/gxgo_export_staging' : '/config/gxgo_export';

export const pastExportsApi = {
  async fetchPastExports() {
    const response = await httpInstances.axiosInternalInstance.get<PastExportsResponse>(
      getExportConfigPath(),
      { validateStatus: status => status === 200 || status === 404 }
    );

    if (response.status === 404) {
      return [];
    }

    return Object.values(response.data)
      .filter(exp => exp.name)
      .map(({ tradeSettings, sourceSettings, ...item }) => ({
        ...item,
        sourceSettings: sourceSettings ?? tradeSettings
      }));
  },
  deletePastExport(exportId: string) {
    return httpInstances.axiosInternalInstance.delete(`${getExportConfigPath()}/${exportId}`);
  }
};
